import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { pmApi } from '../../api/pm.js';

export default function Qa() {
  const { key } = useParams();
  const [project, setProject] = useState(null);
  const [tab, setTab] = useState('cases');
  useEffect(() => { if (key) pmApi.getProject(key).then((d) => setProject(d?.project)); }, [key]);

  if (key && !project) return <div className="muted">Loading…</div>;
  if (!project && !key) return <GlobalQaDashboard />;

  return (
    <section>
      <header className="page-header"><h1>{project.name} · QA</h1></header>
      <div className="tabs">
        <button className={tab === 'cases' ? 'tab active' : 'tab'} onClick={() => setTab('cases')}>Test Cases</button>
        <button className={tab === 'plans' ? 'tab active' : 'tab'} onClick={() => setTab('plans')}>Test Plans</button>
        <button className={tab === 'metrics' ? 'tab active' : 'tab'} onClick={() => setTab('metrics')}>Metrics</button>
        <button className={tab === 'defects' ? 'tab active' : 'tab'} onClick={() => setTab('defects')}>Defects</button>
      </div>
      {tab === 'cases' && <Cases project={project} />}
      {tab === 'plans' && <Plans project={project} />}
      {tab === 'metrics' && <Metrics project={project} />}
      {tab === 'defects' && <Defects project={project} />}
    </section>
  );
}

function GlobalQaDashboard() {
  const [projects, setProjects] = useState([]);
  const [pid, setPid] = useState(null);
  useEffect(() => { pmApi.projects().then((d) => { setProjects(d?.items || []); if (d?.items?.[0]) setPid(d.items[0].id); }); }, []);
  if (!projects.length) return <div className="muted">Create a project first.</div>;
  return (
    <section>
      <header className="page-header">
        <h1>QA Dashboard</h1>
        <select value={pid || ''} onChange={(e) => setPid(e.target.value)}>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.key} · {p.name}</option>)}
        </select>
      </header>
      {pid && <Metrics project={{ id: pid }} />}
    </section>
  );
}

function Cases({ project }) {
  const [suites, setSuites] = useState([]);
  const [cases, setCases] = useState([]);
  const [activeSuite, setActiveSuite] = useState(null);
  const [showSuite, setShowSuite] = useState(false);
  const [showCase, setShowCase] = useState(false);

  function refresh() { pmApi.testSuites(project.id).then((d) => setSuites(d?.items || [])); }
  useEffect(refresh, [project.id]);
  useEffect(() => { if (activeSuite) pmApi.testCases(activeSuite).then((d) => setCases(d?.items || [])); else setCases([]); }, [activeSuite]);

  async function newSuite(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    await pmApi.createTestSuite({ projectId: project.id, name: fd.get('name'), description: fd.get('description') || null });
    e.currentTarget.reset(); setShowSuite(false); refresh();
  }
  async function newCase(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const stepsRaw = fd.get('steps') || '';
    const steps = stepsRaw.split('\n').filter(Boolean).map((s) => ({ step: s }));
    await pmApi.createTestCase({
      suiteId: activeSuite, title: fd.get('title'),
      preconditions: fd.get('preconditions') || null,
      steps, expectedResult: fd.get('expected') || null,
      priority: fd.get('priority') || 'medium',
    });
    e.currentTarget.reset(); setShowCase(false);
    pmApi.testCases(activeSuite).then((d) => setCases(d?.items || []));
  }

  return (
    <div className="grid-2">
      <div className="card">
        <header className="page-header"><h3>Suites</h3><button className="btn ghost small" onClick={() => setShowSuite((s) => !s)}>{showSuite ? '×' : '+'}</button></header>
        {showSuite && (
          <form onSubmit={newSuite} className="grid-form">
            <label>Name<input name="name" required /></label>
            <label>Description<input name="description" /></label>
            <div className="full actions"><button className="btn primary" type="submit">Create</button></div>
          </form>
        )}
        <ul className="suite-list">
          {suites.map((s) => (
            <li key={s.id} className={activeSuite === s.id ? 'active' : ''} onClick={() => setActiveSuite(s.id)}>{s.name}</li>
          ))}
          {suites.length === 0 && <li className="muted">No suites yet.</li>}
        </ul>
      </div>

      <div className="card">
        <header className="page-header">
          <h3>Cases {cases.length > 0 && `(${cases.length})`}</h3>
          {activeSuite && <button className="btn ghost small" onClick={() => setShowCase((s) => !s)}>{showCase ? '×' : '+'}</button>}
        </header>
        {!activeSuite && <div className="muted">Pick a suite.</div>}
        {showCase && activeSuite && (
          <form onSubmit={newCase} className="grid-form">
            <label className="full">Title<input name="title" required /></label>
            <label>Priority<select name="priority" defaultValue="medium"><option>critical</option><option>high</option><option>medium</option><option>low</option></select></label>
            <label className="full">Preconditions<textarea name="preconditions" rows={2} /></label>
            <label className="full">Steps (one per line)<textarea name="steps" rows={4} required /></label>
            <label className="full">Expected result<textarea name="expected" rows={2} /></label>
            <div className="full actions"><button className="btn primary" type="submit">Create</button></div>
          </form>
        )}
        <ul className="case-list">
          {cases.map((c) => (
            <li key={c.id}>
              <strong>{c.title}</strong>
              <span className={`chip prio-${c.priority}`}>{c.priority}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function Plans({ project }) {
  const [plans, setPlans] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [plan, setPlan] = useState(null);
  const [showForm, setShowForm] = useState(false);

  function refresh() { pmApi.testPlans(project.id).then((d) => setPlans(d?.items || [])); }
  useEffect(refresh, [project.id]);
  useEffect(() => { if (activeId) pmApi.getTestPlan(activeId).then((d) => setPlan(d?.plan)); }, [activeId]);

  async function newPlan(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const created = await pmApi.createTestPlan({ projectId: project.id, name: fd.get('name'), version: fd.get('version') || null });
    setActiveId(created.plan.id); setShowForm(false); e.currentTarget.reset(); refresh();
  }

  async function execute(planCaseId, caseId, status) {
    const actual = window.prompt(`Notes for ${status}?`) || '';
    await pmApi.executeTest({ planId: activeId, caseId, status, actualResult: actual });
    pmApi.getTestPlan(activeId).then((d) => setPlan(d?.plan));
  }

  return (
    <div>
      <header className="page-header">
        <h3>Test Plans</h3>
        <button className="btn primary small" onClick={() => setShowForm((s) => !s)}>{showForm ? 'Close' : '+ New Plan'}</button>
      </header>
      {showForm && (
        <form onSubmit={newPlan} className="card form-card grid-form">
          <label>Name<input name="name" required /></label>
          <label>Version<input name="version" /></label>
          <div className="full actions"><button className="btn primary" type="submit">Create</button></div>
        </form>
      )}
      <select value={activeId || ''} onChange={(e) => setActiveId(e.target.value)} style={{ marginBottom: 12 }}>
        <option value="">Select a plan…</option>
        {plans.map((p) => <option key={p.id} value={p.id}>{p.name}{p.version ? ` v${p.version}` : ''} · {p.status}</option>)}
      </select>
      {plan && (
        <div className="card">
          <h3>{plan.name}{plan.version ? ` v${plan.version}` : ''}</h3>
          <table className="data-table">
            <thead><tr><th>Case</th><th>Priority</th><th>Assignee</th><th></th></tr></thead>
            <tbody>
              {plan.cases.map((c) => (
                <tr key={c.plan_case_id}>
                  <td>{c.title}</td>
                  <td><span className={`chip prio-${c.priority}`}>{c.priority}</span></td>
                  <td className="muted">{c.assignee_name || 'unassigned'}</td>
                  <td>
                    <button className="btn primary small" onClick={() => execute(c.plan_case_id, c.id, 'pass')}>Pass</button>
                    <button className="btn ghost small danger-btn" onClick={() => execute(c.plan_case_id, c.id, 'fail')}>Fail</button>
                    <button className="btn ghost small" onClick={() => execute(c.plan_case_id, c.id, 'skip')}>Skip</button>
                  </td>
                </tr>
              ))}
              {plan.cases.length === 0 && <tr><td colSpan={4} className="muted">No cases in this plan yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Metrics({ project }) {
  const [m, setM] = useState(null);
  useEffect(() => { pmApi.qaMetrics(project.id).then(setM); }, [project.id]);
  if (!m) return <div className="muted">Loading…</div>;

  const pass = m.executionMix.pass || 0;
  const fail = m.executionMix.fail || 0;
  const skip = m.executionMix.skip || 0;
  const blocked = m.executionMix.blocked || 0;
  const total = pass + fail + skip + blocked || 1;

  return (
    <div className="card">
      <div className="qa-stats">
        <Stat label="Issues" value={m.issues} />
        <Stat label="Test cases" value={m.testCases} />
        <Stat label="Coverage" value={`${m.coverage}%`} />
        <Stat label="Defects" value={m.defects} />
        <Stat label="Defect density" value={m.defectDensity} subtitle="defects / SP" />
        <Stat label="MTTR" value={`${m.mttrHours}h`} subtitle="avg resolution" />
      </div>

      <h3>Execution mix ({total} runs)</h3>
      <Donut pass={pass} fail={fail} skip={skip} blocked={blocked} />
    </div>
  );
}

function Stat({ label, value, subtitle }) {
  return (
    <div className="stat">
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}</div>
      {subtitle && <div className="muted small">{subtitle}</div>}
    </div>
  );
}

function Donut({ pass, fail, skip, blocked }) {
  const total = pass + fail + skip + blocked;
  if (total === 0) return <div className="muted">No executions yet.</div>;
  const r = 60, c = 2 * Math.PI * r;
  const passLen = (pass / total) * c;
  const failLen = (fail / total) * c;
  const skipLen = (skip / total) * c;
  const blockedLen = (blocked / total) * c;
  return (
    <div className="donut-wrap">
      <svg width="160" height="160" viewBox="0 0 160 160">
        <circle cx="80" cy="80" r={r} fill="none" stroke="#e2e8f0" strokeWidth="22" />
        <Slice cx={80} cy={80} r={r} c={c} offset={0} length={passLen} stroke="#16a34a" />
        <Slice cx={80} cy={80} r={r} c={c} offset={passLen} length={failLen} stroke="#dc2626" />
        <Slice cx={80} cy={80} r={r} c={c} offset={passLen + failLen} length={skipLen} stroke="#94a3b8" />
        <Slice cx={80} cy={80} r={r} c={c} offset={passLen + failLen + skipLen} length={blockedLen} stroke="#f59e0b" />
        <text x="80" y="78" textAnchor="middle" fontSize="20" fontWeight="700" fill="#0f172a">{Math.round((pass / total) * 100)}%</text>
        <text x="80" y="98" textAnchor="middle" fontSize="11" fill="#64748b">pass rate</text>
      </svg>
      <ul className="legend">
        <li><span className="dot" style={{ background: '#16a34a' }} /> Pass · {pass}</li>
        <li><span className="dot" style={{ background: '#dc2626' }} /> Fail · {fail}</li>
        <li><span className="dot" style={{ background: '#94a3b8' }} /> Skip · {skip}</li>
        <li><span className="dot" style={{ background: '#f59e0b' }} /> Blocked · {blocked}</li>
      </ul>
    </div>
  );
}

function Slice({ cx, cy, r, c, offset, length, stroke }) {
  return (
    <circle
      cx={cx} cy={cy} r={r} fill="none" stroke={stroke} strokeWidth="22"
      strokeDasharray={`${length} ${c}`}
      strokeDashoffset={-offset}
      transform={`rotate(-90 ${cx} ${cy})`}
    />
  );
}

function Defects({ project }) {
  const [items, setItems] = useState([]);
  function refresh() { pmApi.defects({ project_id: project.id }).then((d) => setItems(d?.items || [])); }
  useEffect(refresh, [project.id]);
  return (
    <div className="card">
      <table className="data-table">
        <thead><tr><th>Summary</th><th>Severity</th><th>Status</th><th>Created</th></tr></thead>
        <tbody>
          {items.map((d) => (
            <tr key={d.id}>
              <td>{d.summary}</td>
              <td><span className={`chip prio-${d.severity}`}>{d.severity}</span></td>
              <td><span className={`chip status-${d.status === 'closed' ? 'approved' : d.status === 'open' ? 'rejected' : 'pending'}`}>{d.status}</span></td>
              <td className="muted">{new Date(d.created_at).toLocaleDateString()}</td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={4} className="muted">No defects yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
